import React, { useEffect, useRef, useState } from "react";

const faqData = {
    partners: [
        {
            id: 1,
            question: "Who can become a partner with Elystrion?",
            answer:
                "Any legitimate business in the luxury, vintage or collectibles industry such as jewelry brands, gem suppliers, auction houses and resellers can apply for partnership, subject to KYB (Know Your Business) verification.",
        },
        {
            id: 2,
            question: "What are the benefits of becoming a partner?",
            answer:
                "Partners gain access to blockchain verification services, enhanced credibility, expanded market reach and comprehensive authentication tools for premium assets.",
        },
        {
            id: 3,
            question: "How does the partnership application process work?",
            answer:
                "Submit your application through our partner portal, complete KYB verification, undergo our vetting process and receive approval within 7-10 business days.",
        },
        {
            id: 4,
            question: "What are the partnership requirements?",
            answer:
                "Partners must have a legitimate business license, proven track record in luxury goods, meet our quality standards and complete compliance verification.",
        },
        {
            id: 5,
            question: "How do partners integrate with our platform?",
            answer:
                "We provide comprehensive API documentation, integration support, training materials and dedicated account management for seamless platform integration.",
        },
        {
            id: 6,
            question: "What support do partners receive?",
            answer:
                "Partners receive 24/7 technical support, marketing resources, training programs and dedicated account management to ensure success.",
        },
        {
            id: 7,
            question: "Are there partnership fees or commissions?",
            answer:
                "Partnership structure varies based on business model and volume. Contact our partnership team for detailed pricing and commission information.",
        },
        {
            id: 8,
            question: "Can partnerships be terminated?",
            answer:
                "Either party may terminate the partnership with proper notice as outlined in the partnership agreement, subject to completion of ongoing obligations.",
        },
    ],
    customers: [
        {
            id: 1,
            question: "How do I verify the authenticity of my luxury item?",
            answer:
                "Scan your item’s QR code or enter the serial number along with its partner code on our platform. Elystrion will instantly display all verified details and artifacts secured through blockchain technology.",
        },
        {
            id: 2,
            question: "What types of items can customers verify?",
            answer:
                "We verify luxury goods including fine jewelry, diamonds, gemstones, vintage collectibles, designer goods, art pieces and other high-value items.",
        },
        {
            id: 3,
            question: "How long does customer verification take?",
            answer:
                "Standard verification typically takes 3-5 business days, while expedited services are available for urgent requests within 24-48 hours.",
        },
        {
            id: 4,
            question: "What are the costs for customers?",
            answer:
                "Verification costs vary based on item type and complexity.",
        },
        {
            id: 5,
            question: "Can I access my verification history?",
            answer:
                "Yes, customers can access complete verification history and authenticity certificates through our secure blockchain-based customer portal.",
        },
        {
            id: 6,
            question: "What happens if my item fails verification?",
            answer:
                "If an item doesn't meet authenticity standards, we provide detailed reports explaining findings and offer guidance on next steps or alternative options.",
        },
        {
            id: 7,
            question: "Is my personal information secure?",
            answer:
                "We use enterprise-grade security measures and blockchain technology to ensure your personal information and item details remain completely secure and private.",
        },
        {
            id: 8,
            question: "Can I get a refund if unsatisfied?",
            answer:
                "We offer a satisfaction guarantee. If you're not satisfied with our verification service, contact our support team within 30 days for resolution options.",
        },
    ],
}

const FAQSection = () => {
    const [selectedCategory, setSelectedCategory] = useState<"partners" | "customers">("partners")
    const [showAll, setShowAll] = useState(false)
    const [expandedItems, setExpandedItems] = useState<number[]>([]) // First item expanded by default
    const contentRef = useRef<HTMLDivElement>(null);

    const currentFAQs = faqData[selectedCategory]
    const displayedFAQs = showAll ? currentFAQs : currentFAQs.slice(0, 3)

    const toggleExpand = (id: number) => {
        setExpandedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
    }

    const handleCategoryChange = (category: "partners" | "customers") => {
        setSelectedCategory(category)
        setExpandedItems([1]) // Reset to first item expanded
        setShowAll(false) // Reset show all state
    }

    return (
        <section className="h-screen w-full pt-[60px] pb-[60px] xl:pt-[120px] xl:pb-[120px] bg-[#130d01]" id="faq">
            <div className="inner-wrap pl-[25px] pr-[25px] lg:pl-[25px] lg:pr-[25px] xl:pl-[50px] xl:pr-[50px] lg:max-w-[1080px] m-auto m-0">
                <div className="content-block flex-col xl:flex gap-x-36">
                    <div className="left w-full flex xl:flex-col gap-9 mb-[30px] xl:mb[0] items-center justify-center">

                        <p className="tag-line text-center leading-tight">
                            <h2 className="text-[#f3daa3] text-[24px] sm:text-[24px] md:text-[30px] uppercase pb-[30px]">
                                FAQ
                            </h2>
                            <h3 className="text-[#CACACA] text-[32px] sm:text-[46px] md:text-[72px] adjust-width font-semi-bold leading-tight">
                                Frequently Asked Questions
                            </h3>
                        </p>

                    </div>

                    <div className="right w-full">

                        <div className="left w-full flex items-center justify-center gap-12 mb-[50px] xl:mb[0]">
                            <span
                                className={`cursor-pointer transition-colors duration-300 text-[26px] sm:text-[26px] md:text-[36px] font-semi-bold adjust-width pt-[8px] pb-[8px] pl-[20px] pr-[20px] ${selectedCategory === "partners" ? "text-[#C68804] border-solid border-b-4 border-[#C68804] pl-[20px] pr-[20px]" : "text-[#CACACA] hover:text-[#C68804]"
                                    }`}
                                onClick={() => handleCategoryChange("partners")}
                            >
                                Partners
                            </span>
                            <span
                                className={`cursor-pointer transition-colors duration-300 text-[26px] sm:text-[26px] md:text-[36px] font-semi-bold adjust-width pt-[8px] pb-[8px] pl-[20px] pr-[20px] ${selectedCategory === "customers" ? "text-[#C68804] border-solid border-b-4 border-[#C68804] pl-[20px] pr-[20px]" : "text-[#CACACA] hover:text-[#C68804]"
                                    }`}
                                onClick={() => handleCategoryChange("customers")}
                            >
                                Customers
                            </span>
                        </div>


                        <ul>
                            {displayedFAQs.map((faq) => (
                                <li key={faq.id} className={`cursor-pointer ${expandedItems.includes(faq.id) ? 'active' : ""}`} onClick={() => toggleExpand(faq.id)}>
                                    <div className="question cursor-pointer">
                                        <span className="text-[#CACACA] text-[24px] sm:text-[28px] md:text-[32px] adjust-width">
                                            {faq.question}
                                        </span>
                                        <div>
                                            {expandedItems.includes(faq.id) && (
                                                <span className={`cursor-pointer collapsed block`}><svg xmlns="http://www.w3.org/2000/svg" width="36" height="1" viewBox="0 0 36 1" fill="none">
                                                    <rect width="36" height="1" fill="#D9D9D9" />
                                                </svg></span>
                                            )}

                                            <span className={`cursor-pointer expand ${expandedItems.includes(faq.id) ? "hidden" : "block"}`}><svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37" fill="none">
                                                <path d="M18.5 17.5H36.5V18.5H18.5V36.5H17.5V18.5H0.5V17.5H17.5V0.5H18.5V17.5Z" fill="white" fill-opacity="0.75" />
                                            </svg></span>
                                        </div>
                                    </div>

                                    <div
                                        ref={contentRef}
                                        className="overflow-hidden transition-[height] duration-500 ease-out"
                                        style={{
                                            height: expandedItems.includes(faq.id)
                                                ? contentRef.current
                                                    ? `${contentRef.current.scrollHeight}px` // real height if ref exists
                                                    : "80px" // fallback default height for first render
                                                : "0px",
                                        }}
                                    >
                                        <span className="text-[#CACACA] text-[16px] leading-relaxed">{faq.answer}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="flex flex-row w-fulll items-center justify-center">
                            {!showAll && currentFAQs.length > 5 && (
                                <button className="grey get-started mt-[30px]" onClick={() => setShowAll(true)}>
                                    <span>Show more</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <rect width="24" height="24" rx="12" fill="black" />
                                        <path d="M7 17L17 7M7 17H16M7 17V8" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            )}

                            {showAll && (
                                <button className="grey get-started mt-[30px]" onClick={() => setShowAll(false)}>
                                    <span>Show less</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <rect width="24" height="24" rx="12" fill="black" />
                                        <path d="M17 7L7 17M17 7H8M17 7V16" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default FAQSection;